import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useNavigate, useLocation, useParams } from 'react-router-dom';
import { 
  Hexagon, Calendar, Users, Clock, CheckSquare, Settings,
  BarChart3, LogOut, Menu, X, CalendarDays, Building2,
  ChevronLeft, ChevronRight, Palmtree, Landmark, ArrowLeft,
  CalendarCheck, FileText, CircleCheck, CircleDashed, Home, Heart,
  Briefcase, Plane, Baby, GraduationCap, Stethoscope, Coffee, Umbrella, Sun, Thermometer,
  XCircle, Plus, Cake, KeyRound
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '../components/ui/hover-card';
import Sidebar from '../components/Sidebar';
import RequestLeaveDialog from '../components/RequestLeaveDialog';
import QuickBookPopover from '../components/QuickBookPopover';
import AllowanceExplainer from '../components/AllowanceExplainer';
import { useAuth } from '../context/AuthContext';
import { leaveApi, usersApi, holidaysApi, blackoutsApi, settingsApi } from '../lib/api';
import { formatLeave, unitLabel, resolveUnit } from '../lib/leaveUnits';
import { leaveTypeIconComponent } from '../lib/leaveTypeIcons';
import { iconOn, numberOn } from '../lib/leaveColour';
import { toast } from 'sonner';
import BrandedToaster from '../components/BrandedToaster';
import OtherLeaveTaken from '../components/OtherLeaveTaken';
import ToilPanel from '../components/toil/ToilPanel';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, getDay, isToday, parseISO } from 'date-fns';
// ⚠️ Pure, and CHECKED — `node src/lib/workingDays.checks.mjs`. Replaces date-fns
// `isWeekend`, which is Saturday and Sunday for everybody on earth.
import { isNonWorkingDay } from '../lib/workingDays';
import CarryOverLine from '../components/CarryOverLine';
import { currentLeaveYear, leaveYearHeading, leaveYearLabel, calendarYearsOf, inLeaveYear,
  leaveYearSpan, leaveYearMonths, leaveYearMonthIndex, leaveYearInitials, leaveYearRange } from '../lib/leaveYear';
import { approvedBy, bookedBy } from '../lib/bookingOutcome';
import { bookingLength, withTimes, dayShare } from '../lib/bookingTime';
import { dayPortion } from '../lib/dayPortion';
import TimedDayFill from '../components/TimedDayFill';
import { DayMark, DayNumberOver } from '../components/DayMark';
import CancelLeaveDialog from '../components/CancelLeaveDialog';

// ONE icon map for the whole app (lib/leaveTypeIcons) — the per-page copies
// only knew ~15 of the Settings picker's ~50 icons, so changing a leave
// type's icon silently didn't show here (Rob, 16 Jul).
const getLeaveTypeIcon = (iconName, className = "w-5 h-5 text-white") => {
  const IconComponent = leaveTypeIconComponent(iconName);
  return <IconComponent className={className} />;
};

const UserLeavePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userId } = useParams();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [targetUser, setTargetUser] = useState(null);
  // The cancel box (DESIGN-part-cancel.md): the whole booking or just some of its days.
  const [cancelId, setCancelId] = useState(null);
  // Which days this employee works. null = not loaded; `lib/workingDays` owns the single
  // Monday-to-Friday fallback, so do not default it here as well.
  const [workingDays, setWorkingDays] = useState(null);
  const [requests, setRequests] = useState([]);
  const [allowances, setAllowances] = useState([]);
  const [bankHolidays, setBankHolidays] = useState([]);
  const [takenByType, setTakenByType] = useState([]);
  const [bankHolidaysIncluded, setBankHolidaysIncluded] = useState(false);
  const [leaveYearStartMonth, setLeaveYearStartMonth] = useState(1);
  // The bank holidays of the BALANCE's leave year, for the figures printed beside it —
  // not `bankHolidays`, which is the calendar year the planner is showing (29/09/2026).
  const [balanceBankHolidays, setBalanceBankHolidays] = useState([]);
  const [balanceYear, setBalanceYear] = useState(null);
  const [blackouts, setBlackouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  // The planner shows the company's LEAVE year (Rob 6B, 30/10/2026), numbered like
  // LeaveAllowance.year. The first load moves to the leave year we're in — at an April company
  // in February that is LAST calendar year's number. Once only; after that the arrows decide.
  const yearSettled = useRef(false);
  
  // Request Leave dialog state
  const [requestDialogOpen, setRequestDialogOpen] = useState(false);
  const [quickBookDate, setQuickBookDate] = useState(null);
  const [quickBookLeaveTypeId, setQuickBookLeaveTypeId] = useState(null);

  // Role-based access
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  const isManager = user?.role === 'manager' || isAdmin;
  
  // ⚠️ WHO MAY BOOK HERE, AND FOR WHICH DAYS (Rob, 20/10/2026, A + managers). The server
  // answers (GET /leave/can-book-for — `may_book_for`, the same rule that accepts or refuses
  // the booking): admins for anyone, managers for people they manage when they have
  // "Book leave for team". Whoever may book here may also pick PAST days — that is how leave
  // is recorded after the event, and the wallchart already allowed it. It used to be
  // "admins only, never before today", so Harvey's Monday couldn't be booked on Tuesday.
  const [canBookForUser, setCanBookForUser] = useState(false);

  useEffect(() => {
    fetchData();
  // Runs once on mount. `fetchData` is recreated every render, so listing it would re-fetch endlessly.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear, userId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // ⚠️ THE LEAVE YEAR'S START MONTH COMES FIRST (Rob 6B, 30/10/2026): it decides which dates
      // the planner asks for. It used to be fetched after everything else, for the balance only.
      const settingsRes = await settingsApi.get().catch(() => ({ data: null }));
      const _sm = (settingsRes.data && settingsRes.data.leave_year_start_month) || 1;
      setBankHolidaysIncluded(!!(settingsRes.data && settingsRes.data.bank_holidays_included));
      setLeaveYearStartMonth(_sm);
      if (!yearSettled.current) {
        yearSettled.current = true;
        const _thisLeaveYear = currentLeaveYear(_sm);
        if (_thisLeaveYear !== selectedYear) {
          setSelectedYear(_thisLeaveYear);  // fetches again, for that year
          return;
        }
      }
      const [startDate, endDate] = leaveYearRange(_sm, selectedYear);
      
      // Fetch target user info and their leave data
      const [userRes, requestsRes, allowancesRes, holidaysRes, blackoutsRes,
             scheduleRes] = await Promise.all([
        usersApi.get(userId),
        leaveApi.listRequests({ user_id: userId, start_date: startDate, end_date: endDate }),
        leaveApi.getUserAllowances(userId),
        // The planner's bank holidays: every calendar year the leave year touches, kept to the
        // ones inside it (the holidays API answers per calendar year).
        Promise.all(calendarYearsOf(_sm, selectedYear).map((y) => holidaysApi.list(y, userId)))
          .then((rs) => ({ data: rs.flatMap((r) => (r && r.data) || []).filter((h) => inLeaveYear(h.date, _sm, selectedYear)) })),
        blackoutsApi.list(false).catch(() => ({ data: [] })),
        // ⚠️ THEIR RESOLVED WEEK — own pattern, else their location's, else the company's.
        // `usersApi.get` carries only their personal override, which is null for anybody
        // who simply follows their office, so it cannot answer "which days do they work".
        usersApi.getSchedule(userId).catch(() => ({ data: null })),
      ]);
      
      setWorkingDays(scheduleRes?.data?.current?.working_days || null);
      const canBookRes = await leaveApi.canBookFor(userId).catch(() => ({ data: { can_book: false } }));
      setCanBookForUser(!!canBookRes?.data?.can_book);
      setTargetUser(userRes.data);
      setRequests(requestsRes.data);
      setAllowances(allowancesRes.data);
      setBankHolidays(holidaysRes.data || []);
      setBlackouts(blackoutsRes.data || []);

      // ⚠️ THE BALANCE'S LEAVE YEAR, which at an April company spans two calendar years —
      // so fetch each and keep the ones inside it, one per date (the server counts dates).
      const _annual = (allowancesRes.data || []).find((a) => a.leave_type_name === 'Annual Leave');
      const _balYear = (_annual && _annual.year) || currentLeaveYear(_sm);
      const _lists = await Promise.all(calendarYearsOf(_sm, _balYear).map((y) => (
        holidaysApi.list(y, userId).catch(() => ({ data: [] })))));
      const _seen = new Set();
      setBalanceBankHolidays(_lists.flatMap((r) => (r && r.data) || [])
        .filter((h) => inLeaveYear(h.date, _sm, _balYear))
        .filter((h) => (_seen.has(h.date) ? false : _seen.add(h.date))));
      setBalanceYear(_balYear);

      // Leave taken per type for THIS employee (Sick / Unpaid / WFH…) — the
      // "Other leave taken" sidebar breakdown. Separate await so a failure here
      // never blanks the planner.
      const takenRes = await leaveApi.getLeaveTakenByType({ user_id: userId, year: selectedYear }).catch(() => ({ data: [] }));
      setTakenByType(takenRes.data || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      if (error.response?.status === 403) {
        toast.error('You are not Authorised to see this users Year Planner and Statistics');
        navigate('/dashboard');
      } else if (error.response?.status === 404) {
        toast.error('User not found');
        navigate('/dashboard');
      } else {
        toast.error('Failed to load leave data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Generate months for the year
  const months = useMemo(() => {
    // The twelve months of the LEAVE year (Rob 6B, 30/10/2026) — April to March at an April company.
    return leaveYearMonths(leaveYearStartMonth, selectedYear);
  }, [selectedYear, leaveYearStartMonth]);

  // Create a map of dates to leave requests with full details
  const leaveByDate = useMemo(() => {
    const map = {};
    requests.forEach(req => {
      if (req.status === 'cancelled' || req.status === 'declined') return;
      const start = new Date(req.start_date);
      const end = new Date(req.end_date);
      const days = eachDayOfInterval({ start, end });
      days.forEach((day, index) => {
        const key = format(day, 'yyyy-MM-dd');
        map[key] = {
          type: req.leave_type_name,
          color: req.leave_type_color || '#f59e0b',
          icon: req.leave_type_icon || 'calendar',
          status: req.status,
          id: req.id,
          // Half days drawn as on My leave (Rob 4B, 04/11/2026 — one rule, lib/dayPortion)
          day_portion: dayPortion(req, index === 0, index === days.length - 1),
          // Additional details for popover
          start_date: req.start_date,
          end_date: req.end_date,
          start_minute: req.start_minute,   // timed booking (granularity)
          end_minute: req.end_minute,
          total_minutes: req.total_minutes,  // C16: its cost, and the owner's unit
          display_unit: req.display_unit,
          reason: req.reason,
          total_days: req.total_days,
          // H9: a request that approved itself has nobody's name to show.
          actioned_by_name: approvedBy(req),
          actioned_at: req.actioned_at,
          booked_by: bookedBy(req)
        };
      });
    });
    return map;
  }, [requests]);

  // Create a map of bank holidays
  const bankHolidayByDate = useMemo(() => {
    const map = {};
    bankHolidays.forEach(h => {
      map[h.date] = h.name;
    });
    return map;
  }, [bankHolidays]);

  // Helper function to check if a date is blacked out for the target user
  const isBlackoutDate = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const dateObj = new Date(dateStr);
    const userDepartmentId = targetUser?.department_id;
    const targetUserId = targetUser?.id;
    
    for (const blackout of blackouts) {
      const startDate = new Date(blackout.start_date);
      const endDate = new Date(blackout.end_date);
      
      // Check if date falls within blackout range
      if (dateObj >= startDate && dateObj <= endDate) {
        // Check scope
        if (blackout.scope === 'company') {
          return blackout;
        }
        if (blackout.scope === 'department' && blackout.department_id === userDepartmentId) {
          return blackout;
        }
        if (blackout.scope === 'user' && blackout.user_id === targetUserId) {
          return blackout;
        }
      }
    }
    return null;
  };

  // Helper function to get all affected users for a blackout (aggregates same-name blackouts)
  const getBlackoutAffectedUsers = (blackout) => {
    if (blackout.scope !== 'user') return null;
    
    // Find all blackouts with the same name and date range
    const relatedBlackouts = blackouts.filter(b => 
      b.scope === 'user' && 
      b.name === blackout.name && 
      b.start_date === blackout.start_date && 
      b.end_date === blackout.end_date
    );
    
    if (relatedBlackouts.length <= 1) return blackout.user_name;
    
    // Return comma-separated list of names
    return relatedBlackouts
      .map(b => b.user_name)
      .filter(Boolean)
      .join(', ');
  };

  // Handler for quick book - opens dialog with pre-filled date and leave type
  const handleQuickBook = (date, leaveType) => {
    setQuickBookDate(date);
    setQuickBookLeaveTypeId(leaveType.id);
    setRequestDialogOpen(true);
  };

  // The legend: the leave types on this year, in their own colours, and "Pending" only when
  // something is (as Team calendar). It said "Approved Leave" in green, whatever the colours were.
  const legend = useMemo(() => {
    const [from, to] = leaveYearRange(leaveYearStartMonth, selectedYear);
    const types = new Map();
    let pending = false;
    requests.forEach((r) => {
      if (r.status === 'cancelled' || r.status === 'declined') return;
      if (String(r.start_date).slice(0, 10) > to || String(r.end_date).slice(0, 10) < from) return;
      if (!types.has(r.leave_type_name)) types.set(r.leave_type_name, r.leave_type_color || '#f59e0b');
      if (r.status === 'pending') pending = true;
    });
    return { types: Array.from(types.entries()), pending };
  }, [requests, leaveYearStartMonth, selectedYear]);

  // Calculate summary stats
  const summary = useMemo(() => {
    const annualLeave = allowances.find(a => a.leave_type_name === 'Annual Leave') || {};
    const totalAllowance = annualLeave.total_allowance || 0;
    const used = annualLeave.used || 0;
    const pending = annualLeave.pending || 0;
    const remaining = annualLeave.remaining || 0;
    // Carried-over days are part of what can be taken (DESIGN-rollover.md). They were never
    // listed here, so a balance with carry-over didn't add up on this panel (09/10/2026).
    const carried = annualLeave.carried_over || 0;

    // The unit is the VIEWED employee's, not the admin's — this page shows their
    // balance, so it shows it the way they see it. Mirrors MyLeavePage exactly,
    // including the one-unit-per-panel gate for half-backfilled rows.
    const mins = {
      total: annualLeave.total_allowance_minutes,
      used: annualLeave.used_minutes,
      pending: annualLeave.pending_minutes,
      remaining: annualLeave.remaining_minutes,
      carried: annualLeave.carried_over_minutes,
    };
    const totalWithCarryMinutes =
      (mins.total === null || mins.total === undefined || (carried && (mins.carried === null || mins.carried === undefined)))
        ? null
        : mins.total + (mins.carried || 0);
    const deductionsMinutes =
      (mins.used === null || mins.used === undefined || mins.pending === null || mins.pending === undefined)
        ? null
        : mins.used + mins.pending;
    const unit = resolveUnit(
      annualLeave.display_unit, mins.total, totalWithCarryMinutes, mins.used, mins.pending, mins.remaining, deductionsMinutes);


    // Calculate time off by month (booked leave)
    const timeOffByMonth = Array(12).fill(0);
    requests.forEach(req => {
      if (req.status !== 'approved') return;
      const start = new Date(req.start_date);
      const end = new Date(req.end_date);
      const days = eachDayOfInterval({ start, end });
      days.forEach(day => {
        if (inLeaveYear(format(day, 'yyyy-MM-dd'), leaveYearStartMonth, selectedYear)) {
          timeOffByMonth[leaveYearMonthIndex(leaveYearStartMonth, day)] += 1;
        }
      });
    });
    
    // Check if target user works on public holidays
    const userWorksPublicHolidays = targetUser?.works_public_holidays || false;
    
    // Calculate bank holidays by month (only if user doesn't work bank holidays)
    const bankHolidaysByMonth = Array(12).fill(0);
    if (!userWorksPublicHolidays) {
      bankHolidays.forEach(h => {
        const holidayDate = new Date(h.date);
        if (inLeaveYear(h.date, leaveYearStartMonth, selectedYear)) {
          bankHolidaysByMonth[leaveYearMonthIndex(leaveYearStartMonth, holidayDate)] += 1;
        }
      });
    }
    
    // Calculate bank holiday breakdown — only count bank holidays that fall
    // within this person's employment (a mid-year joiner didn't "get" the ones
    // that passed before they started).
    const today = new Date();
    const _startDate = targetUser?.start_date ? new Date(targetUser.start_date) : null;
    // ⚠️ The BALANCE's leave year (29/09/2026) — see `balanceBankHolidays`.
    const effectiveBankHolidays = (userWorksPublicHolidays ? [] : balanceBankHolidays)
      .filter(h => !_startDate || new Date(h.date) >= _startDate);
    const bankHolidaysPassed = effectiveBankHolidays.filter(h => new Date(h.date) < today).length;
    const bankHolidaysRemaining = effectiveBankHolidays.filter(h => new Date(h.date) >= today).length;
    const nextBankHoliday = effectiveBankHolidays
      .filter(h => new Date(h.date) >= today)
      .sort((a, b) => new Date(a.date) - new Date(b.date))[0];
    
    return {
      totalAllowance,
      carried,
      totalWithCarryMinutes,
      used,
      pending,
      remaining,
      unit,
      mins,
      deductionsMinutes,
      timeOffByMonth,
      bankHolidaysByMonth,
      bankHolidayCount: effectiveBankHolidays.length,
      bankHolidaysPassed,
      bankHolidaysRemaining,
      nextBankHoliday,
      userWorksPublicHolidays
    };
  }, [allowances, requests, bankHolidays, balanceBankHolidays, selectedYear, leaveYearStartMonth, targetUser?.works_public_holidays, targetUser?.start_date]);

  // Render a single month calendar
  const renderMonth = (month) => {
    const monthStart = startOfMonth(month);
    const monthEnd = endOfMonth(month);
    const days = eachDayOfInterval({ start: monthStart, end: monthEnd });
    
    // Calculate offset for first day of month (Monday = 0, Sunday = 6)
    const firstDayOfWeek = getDay(monthStart);
    const offset = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;
    
    return (
      <div key={month.toISOString()} className="rounded-2xl border border-hive-line-soft dark:border-border p-3">
        <h3 className="font-heading font-semibold text-[15px] mb-2 px-0.5">
          {format(month, 'MMMM')}
        </h3>
        
        {/* Day headers */}
        <div className="grid grid-cols-7 gap-[3px] mb-1">
          {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((d, i) => (
            <div key={i} className="text-[10.5px] text-center font-semibold text-muted-foreground">
              {d}
            </div>
          ))}
        </div>
        
        {/* Calendar grid */}
        <div className="grid grid-cols-7 gap-[3px]">
          {/* Empty cells for offset */}
          {Array(offset).fill(null).map((_, i) => (
            <div key={`empty-${i}`} className="h-8 sm:h-auto sm:aspect-square" />
          ))}
          
          {/* Day cells */}
          {days.map(day => {
            const dateStr = format(day, 'yyyy-MM-dd');
            const leave = leaveByDate[dateStr];
            const bankHoliday = bankHolidayByDate[dateStr];
            const weekend = isNonWorkingDay(day, workingDays);
            const today = isToday(day);
            
            // Check for blackout
            const blackout = isBlackoutDate(day);
            const isBlackedOut = !!blackout && !leave && !bankHoliday && !weekend;
            
            // Check for birthday - compare month and day only
            const isBirthday = targetUser?.date_of_birth && (() => {
              const dob = new Date(targetUser.date_of_birth);
              return dob.getMonth() === day.getMonth() && dob.getDate() === day.getDate();
            })();
            
            let bgColor = '';
            let icon = null;
            let title = '';
            
            const isPending = leave?.status === 'pending';
            const isHalfDay = !!leave && leave.day_portion !== 'full';
            const isMorning = leave?.day_portion === 'morning';

            if (leave) {
              bgColor = leave.status === 'pending' ? 'bg-amber-500/30' : leave.color;
              title = `${leave.type} (${leave.status})${isHalfDay ? ` - ${isMorning ? 'AM' : 'PM'}` : ''}`;
              icon = getLeaveTypeIcon(leave.icon, `w-4 h-4 ${iconOn(leave.color)}`);
            } else if (bankHoliday) {
              bgColor = 'bg-purple-500';
              title = bankHoliday;
              icon = <Landmark className="w-4 h-4 text-white" />;
            } else if (isBirthday) {
              title = `${targetUser.first_name}'s Birthday! 🎉`;
            } else if (blackout) {
              title = `No-leave dates: ${blackout.name}`;
            }
            
            // Render leave day with hover card
            if (leave) {
              return (
                <HoverCard key={dateStr} openDelay={200} closeDelay={100}>
                  <HoverCardTrigger asChild>
                    <div
                      className={`
                        hh-day h-8 sm:h-auto sm:aspect-square flex items-center justify-center text-[11.5px] font-semibold rounded-[5px] relative overflow-hidden cursor-pointer
                        hover:ring-2 hover:ring-white/50 transition-all
                        ${isHalfDay && dayShare(leave) === null ? 'bg-hive-tile dark:bg-secondary/60' : ''}
                        ${today ? 'ring-2 ring-primary ring-offset-1 ring-offset-card' : ''}
                      `}
                      style={leave.status !== 'pending' && !isHalfDay && dayShare(leave) === null ? { backgroundColor: leave.color } : {}}
                    >
                      {dayShare(leave) !== null ? (
                        // C16: a timed booking is part of a day — same drawing as the wallchart.
                        <div className="w-full h-full relative overflow-hidden rounded-sm">
                          <TimedDayFill color={leave.color} share={dayShare(leave)} pending={leave.status === 'pending'} iconClass="w-2.5 h-2.5" n={day.getDate()} />
                        </div>
                      ) : isHalfDay ? (
                        // Half day: the same diagonal as My leave (Rob 4B, 04/11/2026)
                        <div className="w-full h-full relative overflow-hidden rounded-sm">
                          <div
                            className={`absolute inset-0 ${isPending ? 'opacity-60' : ''}`}
                            style={{
                              backgroundColor: leave.color,
                              clipPath: isMorning
                                ? 'polygon(0 0, 100% 0, 0 100%)' // Top-left triangle for morning
                                : 'polygon(100% 0, 100% 100%, 0 100%)' // Bottom-right triangle for afternoon
                            }}
                          />
                          {isPending && (
                            <div className="absolute inset-0 border border-dashed rounded-sm" style={{ borderColor: leave.color }} />
                          )}
                          <div
                            className={`hh-day-icon absolute flex items-center justify-center ${isMorning ? 'top-0 left-0' : 'bottom-0 right-0'}`}
                            style={{ width: '14px', height: '14px' }}
                          >
                            {getLeaveTypeIcon(leave.icon, `w-3 h-3 ${iconOn(leave.color)}`)}
                          </div>
                          <DayNumberOver n={day.getDate()} numClass="text-foreground hh-day-num-halo" />
                        </div>
                      ) : (
                      <div className={`w-full h-full flex items-center justify-center rounded-sm ${leave.status === 'pending' ? 'bg-amber-500/30 border border-dashed border-amber-500' : ''}`}
                           style={leave.status !== 'pending' ? { backgroundColor: leave.color } : {}}>
                        {icon
                          ? <DayMark n={day.getDate()} numClass={leave.status === 'pending' ? 'text-foreground' : numberOn(leave.color)}>{icon}</DayMark>
                          : <span className="text-foreground font-medium">{day.getDate()}</span>}
                      </div>
                      )}
                    </div>
                  </HoverCardTrigger>
                  <HoverCardContent className="w-72 p-0" align="start">
                    <div className="p-3 border-b border-border" style={{ backgroundColor: leave.color + '20' }}>
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-sm">{leave.type}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          leave.status === 'approved' 
                            ? 'bg-green-500/20 text-green-400' 
                            : 'bg-yellow-500/20 text-yellow-400'
                        }`}>
                          {leave.status === 'approved' ? 'Approved' : 'Pending'}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">{targetUser?.first_name} {targetUser?.last_name}</p>
                    </div>
                    <div className="p-3 space-y-3">
                      {/* Dates */}
                      <div className="flex items-start gap-2">
                        <CalendarCheck className="w-4 h-4 text-muted-foreground mt-0.5" />
                        <div>
                          <p className="text-xs text-muted-foreground">Duration</p>
                          <p className="text-sm">
                            {leave.start_date && leave.end_date ? (
                              leave.start_date === leave.end_date 
                                ? withTimes(format(parseISO(leave.start_date), 'dd MMM yyyy'), leave)
                                : `${format(parseISO(leave.start_date), 'dd MMM')} - ${format(parseISO(leave.end_date), 'dd MMM yyyy')}`
                            ) : dateStr}
                            {!!leave.total_days && (
                              <span className="text-muted-foreground ml-1">({bookingLength(leave)})</span>
                            )}
                          </p>
                        </div>
                      </div>
                      
                      {/* Reason */}
                      {leave.reason && (
                        <div className="flex items-start gap-2">
                          <FileText className="w-4 h-4 text-muted-foreground mt-0.5" />
                          <div>
                            <p className="text-xs text-muted-foreground">Reason</p>
                            <p className="text-sm">{leave.reason}</p>
                          </div>
                        </div>
                      )}
                      
                      {/* Booked by an API key (DESIGN-api-and-webhooks.md step 3, Rob's E A). Only ever
                          there for a booking a key made: the app never said who booked, and this doesn't start. */}
                      {leave.booked_by && (
                        <div className="flex items-start gap-2" data-testid="leave-booked-by">
                          <KeyRound className="w-4 h-4 text-muted-foreground mt-0.5" />
                          <div>
                            <p className="text-xs text-muted-foreground">Booked by</p>
                            <p className="text-sm">{leave.booked_by}</p>
                          </div>
                        </div>
                      )}

                      {/* Approver - only for approved requests */}
                      {leave.status === 'approved' && leave.actioned_by_name && (
                        <div className="flex items-start gap-2">
                          <CircleCheck className="w-4 h-4 text-green-500 mt-0.5" />
                          <div>
                            <p className="text-xs text-muted-foreground">Approved by</p>
                            <p className="text-sm">
                              {leave.actioned_by_name}
                              {leave.actioned_at && (
                                <span className="text-muted-foreground text-xs ml-1">
                                  on {format(parseISO(leave.actioned_at), 'dd MMM yyyy')}
                                </span>
                              )}
                            </p>
                          </div>
                        </div>
                      )}
                      
                      {/* Pending indicator */}
                      {leave.status === 'pending' && (
                        <div className="flex items-start gap-2">
                          <CircleDashed className="w-4 h-4 text-yellow-500 mt-0.5" />
                          <div>
                            <p className="text-xs text-muted-foreground">Status</p>
                            <p className="text-sm text-yellow-400">Awaiting approval</p>
                          </div>
                        </div>
                      )}

                      {/* Cancel — same affordance as the Dashboard wallchart's
                          hover card (Rob, 15 Jul: it was here he expected it).
                          Admins always may; unpermitted managers get the
                          server's 403 toast. */}
                      <button
                        type="button"
                        onClick={() => setCancelId(leave.id)}
                        className="w-full mt-1 flex items-center justify-center gap-1.5 text-xs font-medium text-red-500 hover:text-red-600 hover:bg-red-500/10 rounded-md py-1.5 transition-colors"
                      >
                        <X className="w-3.5 h-3.5" />
                        Cancel this leave
                      </button>
                    </div>
                  </HoverCardContent>
                </HoverCard>
              );
            }
            
            // Render non-leave day (bank holiday, blackout, birthday, or regular)
            // Past days stay bookable here: booking for someone else may be after the event.
            const isPast = day < new Date(new Date().setHours(0, 0, 0, 0));
            const canQuickBook = canBookForUser && !bankHoliday && !isBlackedOut && !weekend && !isBirthday;
            
            return (
              <div
                key={dateStr}
                className={`
                  hh-day h-8 sm:h-auto sm:aspect-square flex items-center justify-center text-[11.5px] font-semibold rounded-[5px] relative overflow-hidden
                  ${weekend && !bankHoliday ? 'bg-hive-weekend dark:bg-background/80 text-muted-foreground' : 'bg-hive-tile dark:bg-secondary/60'}
                  ${today && !bankHoliday ? '!bg-primary !text-primary-foreground' : ''}
                  ${today ? 'ring-2 ring-primary ring-offset-1 ring-offset-card' : ''}
                `}
                title={!isBlackedOut && !isBirthday ? title : undefined}
              >
                {bankHoliday ? (
                  <div className="w-full h-full flex items-center justify-center rounded-sm"
                       style={{ backgroundColor: 'rgb(168, 85, 247)' }}>
                    <DayMark n={day.getDate()} numClass="text-white"><Landmark className="w-4 h-4 text-white" /></DayMark>
                  </div>
                ) : isBirthday ? (
                  (() => {
                    const canBookHere = canBookForUser && !weekend && !isBlackedOut;
                    const ribbon = (
                      <>
                        <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-[8px]" style={{ background: 'linear-gradient(90deg,#b8862f,#f6e08f 42%,#fffbe6 50%,#f6e08f 58%,#b8862f)', boxShadow: '0 0 0 1px rgba(0,0,0,0.12)' }} />
                        <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-[8px]" style={{ background: 'linear-gradient(0deg,#b8862f,#f6e08f 42%,#fffbe6 50%,#f6e08f 58%,#b8862f)', boxShadow: '0 0 0 1px rgba(0,0,0,0.12)' }} />
                        <span className="hh-day-icon absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-[20px] leading-none drop-shadow">🎀</span>
                        <DayNumberOver n={day.getDate()} numClass="text-white hh-day-num-dark-halo" />
                      </>
                    );
                    if (!canBookHere) return (
                      <div className="relative w-full h-full rounded-sm overflow-hidden" style={{ background: 'linear-gradient(135deg,#ef7a7a,#dc5f5f)' }} title={`${targetUser?.first_name}'s Birthday! 🎉`}>
                        {ribbon}
                      </div>
                    );
                    return (
                      <QuickBookPopover date={day} onSelectLeaveType={handleQuickBook}>
                        <button className="relative w-full h-full rounded-sm overflow-hidden hover:ring-2 hover:ring-white/70 transition-all" style={{ background: 'linear-gradient(135deg,#ef7a7a,#dc5f5f)' }} title={`${targetUser?.first_name}'s Birthday! 🎉 \u2014 click to book leave`}>
                          {ribbon}
                        </button>
                      </QuickBookPopover>
                    );
                  })()
                ) : isBlackedOut ? (
                  <HoverCard openDelay={200} closeDelay={100}>
                    <HoverCardTrigger asChild>
                      <div 
                        className="w-full h-full flex items-center justify-center rounded-sm cursor-help"
                        style={{ 
                          background: 'repeating-linear-gradient(45deg, rgba(239,68,68,0.15), rgba(239,68,68,0.15) 3px, rgba(239,68,68,0.3) 3px, rgba(239,68,68,0.3) 6px)',
                          border: '1px solid rgba(239,68,68,0.4)'
                        }}
                      >
                        <DayMark n={day.getDate()} numClass="text-hive-danger-fg dark:text-red-300"><XCircle className="w-5 h-5 text-red-400" /></DayMark>
                      </div>
                    </HoverCardTrigger>
                    <HoverCardContent className="w-56 p-0" align="start">
                      <div className="p-2 border-b border-red-500/30 bg-red-500/10">
                        <span className="font-semibold text-xs text-red-400">🚫 No-leave dates</span>
                      </div>
                      <div className="p-2 space-y-1">
                        <p className="font-medium text-xs">{blackout.name}</p>
                        {blackout.reason && (
                          <p className="text-[10px] text-muted-foreground">{blackout.reason}</p>
                        )}
                        <div className="text-[10px]">
                          <span className="text-muted-foreground">Applies to: </span>
                          <span className="font-medium">
                            {blackout.scope === 'company' && 'Everyone (Company-wide)'}
                            {blackout.scope === 'department' && (blackout.department_name || 'Department')}
                            {blackout.scope === 'user' && (getBlackoutAffectedUsers(blackout) || 'Specific Employee(s)')}
                          </span>
                        </div>
                        <p className="text-[10px] text-muted-foreground">
                          {new Date(blackout.start_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                          {' - '}
                          {new Date(blackout.end_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                        </p>
                        <p className="text-[10px] text-red-400">Leave cannot be booked</p>
                      </div>
                    </HoverCardContent>
                  </HoverCard>
                ) : canQuickBook ? (
                  <QuickBookPopover date={day} onSelectLeaveType={handleQuickBook}>
                    <button 
                      className="w-full h-full flex items-center justify-center rounded-sm hover:bg-primary/20 transition-colors group"
                      title={`Book leave for ${targetUser?.first_name || 'user'}`}
                    >
                      <span className="group-hover:hidden">{day.getDate()}</span>
                      <Plus className="w-4 h-4 text-muted-foreground hidden group-hover:block" />
                    </button>
                  </QuickBookPopover>
                ) : (
                  <span className={isPast ? 'text-muted-foreground/50' : ''}>{day.getDate()}</span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background flex">
      <BrandedToaster />
      
      {/* Shared Sidebar */}
      <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

      {/* Main content */}
      <main className="flex-1 overflow-auto">
        <header className="relative z-30">
          <div className="flex flex-wrap items-end justify-between gap-x-4 gap-y-3 px-4 sm:px-6 lg:px-8 pt-5 pb-1">
            <div className="flex items-center gap-4 min-w-0">
              <button className="lg:hidden" onClick={() => setSidebarOpen(true)}>
                <Menu className="w-6 h-6" />
              </button>
              <div className="min-w-0">
                {/* Back button — a small link above their name (Rob 5A, 30/10/2026) */}
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => navigate(-1)}
                  className="h-auto p-0 mb-1.5 text-muted-foreground hover:bg-transparent hover:text-foreground"
                >
                  <ArrowLeft className="w-3.5 h-3.5 mr-1" />
                  Back
                </Button>
                
                {/* User Avatar and Name — the page's title (Rob 5A) */}
                {targetUser && (
                  <div className="flex items-center gap-3.5 min-w-0">
                    {targetUser.profile_picture_url ? (
                      <img src={targetUser.profile_picture_url} alt={`${targetUser.first_name} ${targetUser.last_name}`} className="w-12 h-12 rounded-full object-cover" />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-hive-orange to-hive-orange-light flex items-center justify-center text-white font-heading font-semibold text-lg">
                        {targetUser.first_name?.[0]}{targetUser.last_name?.[0]}
                      </div>
                    )}
                    <div className="min-w-0">
                      <h1 className="font-heading text-2xl sm:text-[30px] sm:leading-tight font-semibold truncate">
                        {targetUser.first_name} {targetUser.last_name}
                      </h1>
                      <p className="text-sm text-muted-foreground mt-0.5">{targetUser.department_name || 'No department'}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        <div className="p-4 sm:p-6 lg:px-8 space-y-5">
          {loading ? (
            <div className="flex items-center justify-center py-24">
              <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="hh-planner">
              {/* Year Calendar Grid */}
              <div className="hh-planner-year card-hive p-5">
                {/* Year Navigation */}
                <div className="flex items-center gap-3 mb-4">
                  <button 
                    onClick={() => setSelectedYear(y => y - 1)}
                    className="w-9 h-9 rounded-full border border-hive-line dark:border-border bg-card flex items-center justify-center hover:bg-secondary"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <span className="font-heading font-semibold text-lg px-2">
                    {leaveYearSpan(leaveYearStartMonth, selectedYear)}
                  </span>
                  <button 
                    onClick={() => setSelectedYear(y => y + 1)}
                    className="w-9 h-9 rounded-full border border-hive-line dark:border-border bg-card flex items-center justify-center hover:bg-secondary"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 min-[1440px]:grid-cols-6 gap-3">
                  {months.map(month => renderMonth(month))}
                </div>
                
                {/* Legend — the leave types actually on this year, in their own colours (as Team calendar). */}
                <div className="flex flex-wrap gap-x-4 gap-y-2 mt-4 pt-3 border-t border-border text-[12.5px] text-muted-foreground">
                  {bankHolidays.length > 0 && (
                    <div className="flex items-center gap-1.5">
                      <div className="w-4 h-4 rounded bg-purple-500" />
                      <span>Bank holiday</span>
                    </div>
                  )}
                  {blackouts.length > 0 && (
                    <div className="flex items-center gap-1.5">
                      <div 
                        className="w-4 h-4 rounded"
                        style={{ 
                          background: 'repeating-linear-gradient(45deg, rgba(239,68,68,0.15), rgba(239,68,68,0.15) 2px, rgba(239,68,68,0.35) 2px, rgba(239,68,68,0.35) 4px)',
                          border: '1px solid rgba(239,68,68,0.5)'
                        }}
                      />
                      <span>No-leave dates</span>
                    </div>
                  )}
                  {legend.types.map(([name, color]) => (
                    <div key={name} className="flex items-center gap-1.5">
                      <div className="w-4 h-4 rounded" style={{ backgroundColor: color }} />
                      <span>{name}</span>
                    </div>
                  ))}
                  {legend.pending && (
                    <div className="flex items-center gap-1.5">
                      <div className="w-4 h-4 rounded border-2 border-dashed border-muted-foreground opacity-60" />
                      <span>Pending</span>
                    </div>
                  )}
                  <div className="flex items-center gap-1.5">
                    <div className="w-4 h-4 rounded bg-primary" />
                    <span>Today</span>
                  </div>
                </div>
              </div>
              
              {/* Their time in lieu (DESIGN-toil.md screen 7): history, recording it for them,
                  corrections. Nothing when TOIL is off and they've never had any. Under the year, where
                  My leave has "Your pending requests" (Rob, 02/11/2026: full width under everything it
                  sat below an empty gap and looked out of place). */}
              <div className="hh-planner-after">
                <ToilPanel key={userId} userId={userId}
                  userName={targetUser ? `${targetUser.first_name} ${targetUser.last_name}` : ''} />
              </div>

              {/* Right Sidebar Summary */}
              <div className="hh-planner-side">
                {/* Year End Summary Card — portrait beside the year on a big screen, landscape under it
                    on a laptop (Rob 2A + "landscape", 30/10/2026; index.css .hh-planner). */}
                <div className="hh-planner-balance card-hive p-4">
                  <h3 className="font-heading font-semibold mb-4 text-[15px] text-foreground">
                    {/* The year of the BALANCE below, not the calendar being shown
                        (Rob, 29/09/2026; was "Year end Dec {selectedYear}"). */}
                    {leaveYearHeading(leaveYearStartMonth,
                      (allowances.find((a) => a.leave_type_name === 'Annual Leave') || {}).year
                      || currentLeaveYear(leaveYearStartMonth))}
                  </h3>
                  
                  <div className="hh-balance-body">
                  <div>
                  {/* Allowance Section — rendered in the VIEWED employee's unit. */}
                  <div className="space-y-2 mb-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Allowance</span>
                      <span className="font-medium capitalize">{unitLabel({ unit: summary.unit })}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Annual allowance</span>
                      <span className="font-medium">{formatLeave(summary.totalAllowance, { unit: summary.unit, minutes: summary.mins.total, withUnit: false })}</span>
                    </div>
                    {summary.carried !== 0 && (
                      <div className="flex justify-between text-sm">
                        <span>Carried over from last year</span>
                        <span className="font-medium">{formatLeave(summary.carried, { unit: summary.unit, minutes: summary.mins.carried, withUnit: false })}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm font-semibold border-t border-border pt-2 mt-2">
                      <span>Total allowance</span>
                      <span>{formatLeave(summary.totalAllowance + summary.carried, { unit: summary.unit, minutes: summary.totalWithCarryMinutes, withUnit: false })}</span>
                    </div>
                  </div>

                  {/* Deductions Section */}
                  <div className="space-y-2 mb-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Deductions</span>
                      <span className="font-medium capitalize">{unitLabel({ unit: summary.unit })}</span>
                    </div>
                    <div className="flex justify-between text-sm items-center">
                      <span className="flex items-center gap-2">
                        <Palmtree className="w-4 h-4 text-green-500" />
                        Holiday
                      </span>
                      <span className="font-medium">{formatLeave(summary.used, { unit: summary.unit, minutes: summary.mins.used, withUnit: false })}</span>
                    </div>
                    {summary.pending > 0 && (
                      <div className="flex justify-between text-sm items-center text-hive-pending-fg dark:text-hive-orange">
                        <span className="flex items-center gap-2">
                          <Clock className="w-4 h-4" />
                          Pending
                        </span>
                        <span className="font-medium">{formatLeave(summary.pending, { unit: summary.unit, minutes: summary.mins.pending, withUnit: false })}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm font-semibold border-t border-border pt-2 mt-2">
                      <span>Total deductions</span>
                      <span>{formatLeave(summary.used + summary.pending, { unit: summary.unit, minutes: summary.deductionsMinutes, withUnit: false })}</span>
                    </div>
                  </div>

                  </div>
                  <div>
                  {/* Remaining */}
                  <div className="bg-hive-navy dark:bg-[#1C2B52] text-white rounded-2xl p-4 text-center mb-4">
                    <p className="text-sm text-hive-on-navy mb-1 capitalize">
                      {unitLabel({ unit: summary.unit })} remaining
                    </p>
                    {/* `remaining` is ALREADY net of bank holidays (D8 — deducted from
                        the pot server-side, once). Subtracting them again here, as this
                        used to, now DOUBLE-COUNTS. */}
                    <p className="text-4xl font-heading font-bold text-hive-orange">{formatLeave(summary.remaining, { unit: summary.unit, minutes: summary.mins.remaining, withUnit: false })}</p>
                    {!summary.userWorksPublicHolidays && summary.bankHolidaysRemaining > 0 && (
                      <p className="text-xs text-hive-muted-cool mt-2">
                        Plus <span className="text-purple-300 font-medium">{summary.bankHolidaysRemaining}</span> bank holiday {summary.bankHolidaysRemaining === 1 ? 'day' : 'days'}
                      </p>
                    )}
                    {/* How the number was worked out — words, not a grey "?" (Rob C, 01/11/2026). */}
                    <div className="mt-3">
                      <AllowanceExplainer userId={userId} withWords />
                    </div>
                  </div>
                  {/* Carry-over use-by (DESIGN-rollover.md, 09/10/2026) — only before the date. */}
                  <CarryOverLine allowance={allowances.find((a) => a.leave_type_name === 'Annual Leave')} className="mb-4 !mt-0" aboutSomeoneElse />
                  
                  </div>
                  {/* Non-deductible leave - Bank Holidays Section */}
                  <div className="space-y-3 border-t border-border pt-4">
                    <p className="text-sm text-muted-foreground font-medium">Bank holidays ({leaveYearLabel(leaveYearStartMonth, balanceYear || currentLeaveYear(leaveYearStartMonth))})</p>
                    
                    {summary.userWorksPublicHolidays ? (
                      // User works on bank holidays - show notice
                      <div className="p-3 bg-amber-500/10 rounded-lg border border-amber-500/20">
                        <p className="text-sm text-amber-400 font-medium">Works on bank holidays</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Bank holidays don't apply to this employee's schedule.
                        </p>
                      </div>
                    ) : (
                      // User has bank holidays off - show breakdown
                      <>
                        {/* Total Bank Holidays */}
                        <div className="flex justify-between text-sm items-center">
                          <span className="flex items-center gap-2">
                            <Landmark className="w-4 h-4 text-purple-500" />
                            Total this leave year
                          </span>
                          <span className="font-semibold text-purple-700 dark:text-purple-300">{summary.bankHolidayCount} {summary.bankHolidayCount === 1 ? 'day' : 'days'}</span>
                        </div>
                        
                        {/* Bank Holidays Passed */}
                        <div className="flex justify-between text-sm items-center text-muted-foreground">
                          <span className="flex items-center gap-2 pl-6">
                            Already passed
                          </span>
                          <span>{summary.bankHolidaysPassed} {summary.bankHolidaysPassed === 1 ? 'day' : 'days'}</span>
                        </div>
                        
                        {/* Bank Holidays Remaining */}
                        <div className="flex justify-between text-sm items-center">
                          <span className="flex items-center gap-2 pl-6">
                            Still to come
                          </span>
                          <span className="font-medium text-hive-success-fg dark:text-emerald-300">{summary.bankHolidaysRemaining} {summary.bankHolidaysRemaining === 1 ? 'day' : 'days'}</span>
                        </div>
                        
                        {/* Next Bank Holiday */}
                        {summary.nextBankHoliday && (
                          <div className="mt-3 p-3 bg-purple-500/10 rounded-lg border border-purple-500/20">
                            <p className="text-xs text-muted-foreground mb-1">Next bank holiday</p>
                            <p className="font-medium text-sm">{summary.nextBankHoliday.name}</p>
                            <p className="text-xs text-purple-700 dark:text-purple-300">
                              {format(new Date(summary.nextBankHoliday.date), 'EEEE, d MMMM yyyy')}
                            </p>
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  {/* Other (non-deducting) leave taken — Sick / Unpaid / WFH… */}
                  <OtherLeaveTaken items={takenByType} unit={summary.unit} year={leaveYearLabel(leaveYearStartMonth, selectedYear)} />
                  </div>
                </div>

                <div className="hh-planner-more">
                {/* Time Off by Month Chart - Stacked Bar */}
                <div className="card-hive p-4">
                  <h3 className="font-heading font-semibold mb-4 text-[15px] text-foreground">
                    Time off
                  </h3>
                  {(() => {
                    // Calculate max total (leave + bank holidays) for scaling
                    const totalByMonth = summary.timeOffByMonth.map((leave, i) => leave + summary.bankHolidaysByMonth[i]);
                    const maxDays = Math.max(...totalByMonth, 1);
                    
                    return (
                      <div className="flex items-end justify-between h-20 gap-1">
                        {summary.timeOffByMonth.map((leaveDays, i) => {
                          const bankHolidayDays = summary.bankHolidaysByMonth[i];
                          const totalDays = leaveDays + bankHolidayDays;
                          
                          return (
                            <div key={i} className="flex-1 flex flex-col items-center justify-end h-full">
                              {/* Stacked bar container */}
                              <div 
                                className="w-full flex flex-col justify-end"
                                style={{ height: totalDays > 0 ? `${((totalDays) / maxDays) * 100}%` : '2px' }}
                                title={`${leaveDays} leave + ${bankHolidayDays} bank holiday${bankHolidayDays !== 1 ? 's' : ''}`}
                              >
                                {/* Bank holiday portion (purple) - on top */}
                                {bankHolidayDays > 0 && (
                                  <div 
                                    className="w-full bg-purple-500 rounded-t"
                                    style={{ 
                                      flex: `0 0 ${(bankHolidayDays / totalDays) * 100}%`,
                                      minHeight: '4px'
                                    }}
                                  />
                                )}
                                {/* Leave portion (amber/primary) - on bottom */}
                                {leaveDays > 0 && (
                                  <div 
                                    className={`w-full bg-primary ${bankHolidayDays === 0 ? 'rounded-t' : ''}`}
                                    style={{ 
                                      flex: `0 0 ${(leaveDays / totalDays) * 100}%`,
                                      minHeight: '4px'
                                    }}
                                  />
                                )}
                                {/* Empty bar placeholder */}
                                {totalDays === 0 && (
                                  <div className="w-full bg-border/50 rounded-t" style={{ height: '2px' }} />
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                  <div className="flex justify-between mt-2 text-[10px] text-muted-foreground">
                    {leaveYearInitials(leaveYearStartMonth).map((m, i) => (
                      <span key={i} className="flex-1 text-center">{m}</span>
                    ))}
                  </div>
                  {/* Legend */}
                  <div className="flex items-center justify-center gap-4 mt-3 text-xs">
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 bg-primary rounded" />
                      <span className="text-muted-foreground">Leave</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 bg-purple-500 rounded" />
                      <span className="text-muted-foreground">Bank holidays</span>
                    </div>
                  </div>
                </div>
                
                {/* Leave Requests List */}
                <div className="card-hive p-4">
                  <h3 className="font-heading font-semibold mb-4 text-[15px] text-foreground">
                    Leave requests
                  </h3>
                  {requests.filter(r => r.status !== 'cancelled' && r.status !== 'declined').length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">No leave requests</p>
                  ) : (
                    <div className="space-y-3 max-h-64 overflow-y-auto">
                      {requests
                        .filter(r => r.status !== 'cancelled' && r.status !== 'declined')
                        .map(req => (
                          <div key={req.id} className="flex items-center gap-3">
                            <div 
                              className="w-8 h-8 rounded flex items-center justify-center flex-shrink-0"
                              style={{ backgroundColor: `${req.leave_type_color || '#f59e0b'}20` }}
                            >
                              <Palmtree className="w-4 h-4" style={{ color: req.leave_type_color || '#f59e0b' }} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">{req.leave_type_name}</p>
                              <p className="text-xs text-muted-foreground">
                                {withTimes(format(new Date(req.start_date), 'd MMM'), req)}
                                {req.start_date !== req.end_date && ` - ${format(new Date(req.end_date), 'd MMM')}`}
                              </p>
                            </div>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              req.status === 'approved' ? 'bg-hive-success-bg text-hive-success-fg dark:bg-emerald-500/20 dark:text-emerald-300' : 'bg-hive-pending-bg text-hive-pending-text dark:bg-amber-500/20 dark:text-hive-orange-light'
                            }`}>
                              {req.status === 'approved' ? '✓' : '⏳'}
                            </span>
                            {/* Cancel on behalf: the backend has always allowed
                                admins (and permitted managers) to cancel via
                                DELETE /leave/requests/{id} — this list just never
                                offered the button (Rob, 15 Jul). Unpermitted
                                managers get a clean 403 toast from the server. */}
                            <button
                              type="button"
                              title="Cancel this leave"
                              data-testid={`cancel-leave-${req.id}`}
                              className="text-muted-foreground hover:text-red-500 transition-colors flex-shrink-0"
                              onClick={() => setCancelId(req.id)}
                            >
                              <XCircle className="w-4 h-4" />
                            </button>
                          </div>
                        ))
                      }
                    </div>
                  )}
                </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
      
      {/* The cancel box — the whole booking or just some of its days (DESIGN-part-cancel.md). */}
      <CancelLeaveDialog requestId={cancelId} onClose={() => setCancelId(null)} onDone={fetchData} />

      {/* Request Leave Dialog - for booking on behalf of the user */}
      <RequestLeaveDialog 
        open={requestDialogOpen}
        onOpenChange={(open) => {
          setRequestDialogOpen(open);
          if (!open) {
            // Clear quick book values when dialog closes
            setQuickBookDate(null);
            setQuickBookLeaveTypeId(null);
          }
        }}
        onSuccess={fetchData}
        initialDate={quickBookDate}
        initialLeaveTypeId={quickBookLeaveTypeId}
        bookForUserId={userId}
        bookForUserName={targetUser ? `${targetUser.first_name} ${targetUser.last_name}` : null}
        allowPastDates={canBookForUser}
      />
    </div>
  );
};

export default UserLeavePage;
